import moment from 'moment'
import { useFormik } from 'formik'
import { useSelector } from 'react-redux'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ConvertLocalTimeGet, ConvertLocalTimePost, Lookup, insertLog, ValidateDateString } from '~/utils'
import { ClientAPI, VesselForecastAPI, ServiceRequestAPI, VesselAPI, JettyApi } from '~/api'

import validationSchema from './manage-modal.validation'
import { MasterDataAPI } from '../../../../api'

const useHooks = ({ actionType, editData, dataItem, dataState, handleClose, refetchData }) => {
  const isEdit = actionType === 'edit'
  const isView = actionType === 'view'
  const isCreate = actionType === 'create'

  const { Location } = useSelector((state) => state.usersProfileReducer)

  const [loading, setLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [jettyOptions, setJettyOptions] = useState([])
  const [vesselOptions, setVesselOptions] = useState([])
  const [vesselLoading, setVesselLoading] = useState(false)
  const searchRequestRef = useRef(0)
  const [clientOptions, setClientOptions] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [activityOptions, setActivityOptions] = useState([])
  const [isConfirmOverlap, setIsConfirmOverlap] = useState(false)
  const [isOpenCofirmOverlap, setIsOpenCofirmOverlap] = useState(false)
  const [uomQuantities, setUomQuantities] = useState([])

  const [isOpenConfirmAssignment, setIsOpenConfirmAssignment] = useState(false)
  const [showAlert, setAlert] = useState({
    visible: false,
    msg: '',
    success: false,
    type: '',
  })

  const handleCloseConfirmOverlap = useCallback(() => setIsOpenCofirmOverlap(false), [])
  const handleOpenConfirmAssignment = useCallback(() => setIsOpenConfirmAssignment(true), [])
  const handleCloseConfirmAssignment = useCallback(() => setIsOpenConfirmAssignment(false), [])

  const setAlertMessage = (msg, success, type) => {
    setAlert({
      msg,
      success,
      visible: true,
      type,
    })
  }

  const handleSubmit = async (payload) => {
    try {
      setIsSubmitting(true)
      let response
      if (!isEdit) {
        // Add new data
        response = await VesselForecastAPI.InsertVesselForecast(payload)
        if (response.data && response.data.result) {
          setAlertMessage('Add data has been successful', true, 'insert')
        } else {
          setAlertMessage(response.response.data.error, false, 'insert')
        }
      } else {
        // Edit existing data
        response = await VesselForecastAPI.UpdateVesselForecast(payload)
        if (response.data && response.data.result) {
          setAlertMessage('Update data has been successful', true, 'update')
        } else {
          setAlertMessage(response.response.data.error, false, 'update')
        }
      }

      if (response.data.result) {
        FormInput.handleReset()
      }
    } catch (err) {
      setAlertMessage('An error occurred while saving data', false, editData.Id == null ? 'insert' : 'update')
      console.error(err)
      return err
    } finally {
      setIsSubmitting(false)
    }
    return true
  }

  const calculateDuration = (startTime, endTime) => {
    if (startTime && endTime) {
      const start = moment(startTime)
      const end = moment(endTime)
      if (end.isAfter(start)) {
        const diff = moment.duration(end.diff(start))
        return `${Math.floor(diff.asHours())} hours ${diff.minutes()} minutes`
      }
    }
    return ''
  }

  const handleValidateDateValue = (date) => {
    if (ValidateDateString(date)) {
      return ConvertLocalTimeGet(Location.LocationDescription, date, 'YYYY-MM-DDTHH:mm:ss')
    } else {
      return null
    }
  }

  const initialValues = {
    SR_No: (isEdit || isView) ? dataItem.sr_no : '',
    Product: (isEdit || isView) ? dataItem.product : '',
    Quantity: (isEdit || isView) ? dataItem.quantity : '',
    UOM_Code: (isEdit || isView) ? dataItem.uom_code : '',
    Jetty: (isEdit || isView) ? dataItem.jetty_code : undefined,
    Status: (isEdit || isView) ? dataItem.plan_code : undefined,
    Client: (isEdit || isView) ? dataItem.client_code : undefined,
    Activity: (isEdit || isView) ? dataItem.activity_code : undefined,
    Vessel_Name: (isEdit || isView) ? dataItem.vessel_code : undefined,
    All_Fast: (isEdit || isView) ? handleValidateDateValue(dataItem.all_fast_non_formatted) : null,
    Cast_Off: (isEdit || isView) ? handleValidateDateValue(dataItem.cast_off_non_formatted) : null,
    Duration: (isEdit || isView) ? calculateDuration(dataItem.all_fast_non_formatted, dataItem.cast_off_non_formatted) : '',
    Close_Assignment: (isEdit || isView) ? dataItem.is_closed : false,
    Remarks_Vessel_Assignment: (isEdit || isView) ? dataItem.remarks_closed : '',
    Laycan_End: (isEdit || isView) ? handleValidateDateValue(dataItem.laycan_end) : null,
    Laycan_Start: (isEdit || isView) ? handleValidateDateValue(dataItem.laycan_start) : null,
  }

  const FormInput = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          vessel_code: values.Vessel_Name,
          client_code: values.Client,
          jetty_code: values.Jetty,
          all_fast: values.All_Fast && ConvertLocalTimePost(Location.LocationDescription, values.All_Fast),
          cast_off: values.Cast_Off && ConvertLocalTimePost(Location.LocationDescription, values.Cast_Off),
          activity_code: values.Activity,
          product: values.Product,
          quantity: String(values.Quantity),
          uom_code: values.UOM_Code,
          percentage: 0,
          plan_code: values.Status,
          is_active: true,
          laycan_end: values.Laycan_End && ConvertLocalTimePost(Location.LocationDescription, values.Laycan_End),
          laycan_start: values.Laycan_Start && ConvertLocalTimePost(Location.LocationDescription, values.Laycan_Start),
        }
        if (isEdit) {
          payload.id = dataItem.id
          payload.sr_code = dataItem.sr_code,
          payload.is_closed = values.Close_Assignment,
          payload.remarks_closed = values.Remarks_Vessel_Assignment
        }
        await handleSubmit(payload)
      } catch (err) {
        setAlertMessage('Error during form submission', false, isEdit ? 'update' : 'insert')
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleSave = async () => {
    const errors = await FormInput.validateForm()
    FormInput.setTouched(
      Object.keys(FormInput.values).reduce((acc, key) => {
        acc[key] = true
        return acc
      }, {})
    )

    if (Object.keys(errors).length > 0) return // Formik validation failed

    // If vessel assignment is checked, open confirmation modal
    if (FormInput.values.Close_Assignment && FormInput.values.Remarks_Vessel_Assignment.trim() === '') {
      handleOpenConfirmAssignment()
      return
    } else {
      handleCloseConfirmAssignment()
    }

    // Check the overlap jetty validation API before actual submit
    const payload = {
      code: dataItem?.code || '',
      all_fast: FormInput.values.All_Fast && ConvertLocalTimePost(Location.LocationDescription, FormInput.values.All_Fast),
      cast_off: FormInput.values.Cast_Off && ConvertLocalTimePost(Location.LocationDescription, FormInput.values.Cast_Off),
      jetty_code: FormInput.values.Jetty,
    }

    const response = await VesselForecastAPI.GetCheckOverlapJetty(payload)
    if (!response.isOccupied || isConfirmOverlap) {
      insertLog({
        formInput: FormInput,
        action: isEdit ? Lookup.LOG_ACTION.UPDATE : Lookup.LOG_ACTION.ADD,
        refNo: FormInput.values.Code,
        module: Lookup.LOG_MODULE.VESSEL_FORECAST,
      })
      FormInput.handleSubmit()
    } else {
      setIsOpenCofirmOverlap(true)
    }
  }

  const closeResetFormik = () => {
    handleClose()
    FormInput.handleReset()
  }

  const handleCloseAlert = () => {
    setAlert({ ...showAlert, visible: false })
    if (showAlert.success) {
      closeResetFormik()
      refetchData(dataState)
    }
  }

  const handleConfrimOverlapData = () => {
    setIsConfirmOverlap(true)
    setIsOpenCofirmOverlap(false)
    FormInput.handleSubmit()
  }

  const getUomQuantities = async () => {
    try {
      const response = await MasterDataAPI.GetMasterDataType()
      if (
        response &&
        response.data.message.data
      ) {
        const uomQuantities = response.data.message.data.filter((f) => f.uom_type == 'MUT4')
        setUomQuantities(uomQuantities)
      }
    } catch (error) {
      return error
    }
  }

  const fetchListDataVessel = async (keyword) => {
    const requestId = ++searchRequestRef.current
    try {
      setVesselLoading(true)
      const filter = `[{"field":"vessel_name","type":"text","value":"${keyword}"}]`
      const result = await VesselAPI.GetListDataVessel(0, 100, filter)
      if (requestId !== searchRequestRef.current) return
      const data = result.data?.data || result.data
      setVesselOptions(data || [])
    } catch (error) {
      if (requestId !== searchRequestRef.current) return
      console.error('Error fetching vessels:', error)
    } finally {
      if (requestId === searchRequestRef.current) {
        setVesselLoading(false)
      }
    }
  }

  const fetchInitialVessel = async (vesselCode) => {
    if (!vesselCode) return
    try {
      const filter = `[{"field":"code","type":"text","value":"${vesselCode}"}]`
      const result = await VesselAPI.GetListDataVessel(0, 10, filter)
      const data = result.data?.data || result.data
      setVesselOptions(data || [])
    } catch (error) {
      console.error('Error fetching initial vessel:', error)
    }
  }

  const fetchListDataJetty = async () => {
    try {
      const filter = `[{"field":"is_active","type":"bool","value":"true"}, { "type": "number", "value": "${Location.LocationID}", "field": "location_code" }]`
      const result = await JettyApi.GetListDataJetty(0, 1000, filter)
      const data = result.data?.data || result.data
      setJettyOptions(data)
    } catch (error) {
      console.error('Error fetching jetties:', error)
    }
  }

  const fetchAllMasterDataClient = async () => {
    try {
      const filter = '[{"field":"is_active","type":"bool","value":"true"}]'
      const result = await ClientAPI.GetAllMasterDataClient(0, 1000, filter)
      const data = result.data?.data || result.data
      setClientOptions(data)
    } catch (error) {
      console.error('Error fetching clients:', error)
    }
  }

  const getActivityEnums = async () => {
    try {
      const filterString =
        '[{"field":"type","type":"text","value":"Vessel_Activity"},{"field":"is_active","type":"bool","value":"true"}]'
      const response = await ServiceRequestAPI.GetAllFromTableEnum(0, 100, filterString)
      setActivityOptions(response.data)
    } catch (error) {
      console.error('Error fetching enums:', error)
    }
  }

  const getPlanEnums = async () => {
    try {
      const filterString =
        '[{"field":"type","type":"text","value":"Vessel_Plan"},{"field":"is_active","type":"bool","value":"true"}]'
      const response = await ServiceRequestAPI.GetAllFromTableEnum(0, 100, filterString)
      setStatusOptions(response.data)
    } catch (error) {
      console.error('Error fetching enums:', error)
    }
  }

  useEffect(() => {
    const { All_Fast, Cast_Off } = FormInput.values
    const duration = calculateDuration(All_Fast, Cast_Off)
    FormInput.setFieldValue('Duration', duration)
  }, [FormInput.values.All_Fast, FormInput.values.Cast_Off])

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true)
      await Promise.all([
        fetchListDataJetty(),
        fetchAllMasterDataClient(),
        getActivityEnums(),
        getPlanEnums(),
        getUomQuantities(),
      ])
      if (isEdit || isView) {
        await fetchInitialVessel(dataItem?.vessel_code)
      }
      setLoading(false)
    }

    fetchAllData()
  }, [])

  const handleVesselSearch = useMemo(
    () => debounce((inputValue) => {
      if (inputValue.length >= 3) {
        fetchListDataVessel(inputValue)
      } else {
        searchRequestRef.current++
        setVesselOptions([])
        setVesselLoading(false)
      }
    }, 300),
    []
  )

  const handleChangeRemarks = useCallback((e) => {
    FormInput.setFieldValue('Remarks_Vessel_Assignment', e.target.value)
  }, [])

  const handleChangeVesselAssignment = useCallback((e) => {
    FormInput.setFieldValue('Close_Assignment', e.target.checked)

    if (!e.target.checked) {
      FormInput.setFieldValue('Remarks_Vessel_Assignment', '')
    }
  }, [])

  const subTitle = isEdit ? 'Edit Data' : isCreate ? 'Add New Data' : 'Detail'

  return {
    data: {
      loading,
      subTitle,
      isCreate,
      FormInput,
      showAlert,
      isSubmitting,
      jettyOptions,
      clientOptions,
      statusOptions,
      vesselOptions,
      vesselLoading,
      uomQuantities,
      activityOptions,
      isOpenCofirmOverlap,
      isOpenConfirmAssignment,
    },
    methods: {
      handleSave,
      closeResetFormik,
      handleCloseAlert,
      handleChangeRemarks,
      handleVesselSearch,
      handleConfrimOverlapData,
      handleCloseConfirmOverlap,
      handleOpenConfirmAssignment,
      handleCloseConfirmAssignment,
      handleChangeVesselAssignment,
    }
  }
}

export default useHooks
